import { useEffect, useState, useCallback } from "react";
import { LogOut, Calendar, Users, DollarSign, Activity, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import BookingsPanel from "@/components/admin/BookingsPanel";
import ClientsPanel from "@/components/admin/ClientsPanel";
import GalleryPanel from "@/components/admin/GalleryPanel";
import ReferralsPanel from "@/components/admin/ReferralsPanel";
import PricingPanel from "@/components/admin/PricingPanel";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`card-surface p-5 ${accent ? "ring-1 ring-clay/40" : ""}`}>
      <Icon size={18} className={accent ? "text-clay" : "text-forest"} />
      <div className="font-heading text-3xl mt-3">{value}</div>
      <div className="eyebrow mt-1">{label}</div>
    </div>
  );
}

const TABS = ["bookings", "clients", "pricing", "gallery", "referrals"];

export default function AdminDashboard() {
  useSeo({ title: "Dashboard | Full Indoor Air Admin" });
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("bookings");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [s, b, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings", { params: filter ? { status_filter: filter } : {} }),
        api.get("/admin/clients"),
      ]);
      setStats(s.data);
      setBookings(b.data.bookings);
      setClients(c.data.clients);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = useCallback(async (id, newStatus) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status: newStatus });
      load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [load]);

  return (
    <div className="bg-surfaceAlt min-h-screen" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="eyebrow mb-2">Admin Dashboard</div>
            <h1 className="font-heading text-4xl tracking-tighter font-light">
              Welcome, <span className="text-forest">{user?.name || "Admin"}.</span>
            </h1>
          </div>
          <button onClick={logout} className="btn-ghost text-sm" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>

        {err && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-xl">{err}</div>}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="stat-grid">
            <Stat icon={Calendar} label="Total bookings" value={stats.total_bookings} />
            <Stat icon={Activity} label="Pending" value={stats.pending} accent />
            <Stat icon={Users} label="Clients" value={stats.total_clients} />
            <Stat icon={DollarSign} label="Est. revenue" value={`$${(stats.estimated_revenue || 0).toLocaleString()}`} />
          </div>
        )}

        {stats?.bookings_by_city?.length > 0 && (
          <div className="card-surface p-6 mb-10" data-testid="top-cities">
            <div className="eyebrow mb-3">Top cities by bookings</div>
            <div className="flex flex-wrap gap-3">
              {stats.bookings_by_city.map((c) => (
                <div key={`${c.state}-${c.city}`} className="flex items-center gap-2 bg-bone px-4 py-2 rounded-full border border-black/5">
                  <MapPin size={12} className="text-forest" />
                  <span className="text-sm">{c.city}, {c.state}</span>
                  <span className="font-heading text-sm">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2.5 rounded-full text-sm capitalize transition ${
                tab === t ? "bg-forest text-white" : "bg-white border border-black/10 text-ink/75 hover:border-forest/30"
              }`}
              data-testid={`tab-${t}`}
            >{t}</button>
          ))}
        </div>

        {tab === "bookings" && (
          <BookingsPanel bookings={bookings} filter={filter} setFilter={setFilter} onStatusChange={updateStatus} />
        )}
        {tab === "clients" && <ClientsPanel clients={clients} />}
        {tab === "pricing" && <PricingPanel />}
        {tab === "gallery" && <GalleryPanel />}
        {tab === "referrals" && <ReferralsPanel />}
      </div>
    </div>
  );
}
